import { useState, useEffect, useCallback, useRef } from 'react';
import './PerfCharts.css';
import PerfChart from './PerfChart';
import RequiresAviotrackBanner from './RequiresAviotrackBanner';
import { getReceiverStats, getReceiverStatsLatest, getReceiverConnectionEvents } from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';

const RANGE_PRESETS = [
  { label: '15m', seconds: 15 * 60 },
  { label: '1h',  seconds: 60 * 60 },
  { label: '6h',  seconds: 6 * 3600 },
  { label: '24h', seconds: 24 * 3600 },
  { label: '7d',  seconds: 7 * 24 * 3600 },
];

const REFRESH_PRESETS = [
  { label: '10s',  seconds: 10 },
  { label: '30s',  seconds: 30 },
  { label: '1m',   seconds: 60 },
  { label: '5m',   seconds: 300 },
  { label: 'off',  seconds: 0 },
];

const REQUEST_METRICS = [
  'msg_rx_rate', 'msg_dropped',
  // aircraft_modes_now = Mode-S-only aircraft (receiver 1.05.26+); absent on older receivers.
  'aircraft_now', 'aircraft_modes_now', 'aircraft_peak_1m',
  'max_range_nm',
  'rssi_min_dbfs', 'rssi_avg_dbfs', 'rssi_max_dbfs', 'noise_floor_dbfs',
  'current_gain_db',
  'cpu_pct', 'mem_pct', 'temp_c',
  // Input-side drops. Everything else here counts frames the decoder already received, so a
  // pipeline starved of samples charts exactly like a quiet sky. Absent (no line) for
  // receivers whose driver does not measure them, which is the truth rather than a flat zero.
  'dsp_queue_drops', 'dropped_samples',
  // Split by cause (receiver 1.05.05+). These mean completely different things and the combined
  // total cannot distinguish them: ring-full = the decode worker is behind and scales with load;
  // oversized = the transfer was rejected before it was ever queued, structural and flat under
  // load. Absent on older receivers, which the schema guard handles.
  'ring_full_drops', 'oversized_drops',
];

/**
 * Force a one-minute bucket instead of letting the backend pick from the range.
 * pickStep() would give 5 minutes at 6h and 30 at 7d, which averages away exactly the
 * short-lived spikes these charts exist to show — a DSP thread falling behind for ninety
 * seconds disappears entirely into a 30-minute bucket.
 */
const STEP_SECONDS = 60;

/**
 * graphs1090-style perf overview for one receiver. Pulls down-sampled time
 * series + the most recent row, refreshes on a configurable interval.
 *
 * Modeled on History.js → ActivityChart for the chart pattern (custom SVG,
 * theme-aware via CSS variables, no chart library).
 */
/**
 * @param {boolean} [isAviotrack]  false when the receiver has never sent STATS —
 *   every chart here is STATS-derived, so a banner explains the empty page.
 */
function PerfCharts({ receiverUuid, token, isAviotrack, latestReceiverVersion = null, onNavigate = null }) {
  const [rangeSeconds, setRangeSeconds] = useState(60 * 60);   // 1h default
  const [refreshSeconds, setRefreshSeconds] = useState(60);   // 1m default
  const [series, setSeries] = useState({});
  const [restarts, setRestarts] = useState([]);
  const [latest, setLatest] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  // Set when the backend could not fill the requested window and slid it back onto the
  // receiver's most recent data instead. The charts are real, but they are NOT "the last hour".
  const [anchoredRange, setAnchoredRange] = useState(null);

  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async () => {
    if (!receiverUuid || !token) return;
    setLoading(true);
    setError(null);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - rangeSeconds * 1000);
      const [statsResp, latestResp, eventsResp] = await Promise.all([
        getReceiverStats(receiverUuid, from.toISOString(), to.toISOString(), STEP_SECONDS, REQUEST_METRICS, token),
        getReceiverStatsLatest(receiverUuid, token),
        getReceiverConnectionEvents(receiverUuid, token),
      ]);
      if (!aliveRef.current) return;
      if (isApiSuccess(statsResp)) {
        setSeries(statsResp.series || {});
        setRestarts(statsResp.restarts || []);
        setAnchoredRange(statsResp.anchoredToLastData
          ? { from: statsResp.fromTs, to: statsResp.toTs }
          : null);
      } else {
        setError('Failed to load stats series');
      }
      if (isApiSuccess(latestResp)) {
        setLatest(latestResp);
      }
      if (isApiSuccess(eventsResp)) {
        setEvents(eventsResp.events || []);
      }
      setUpdatedAt(new Date());
    } catch (e) {
      if (aliveRef.current) setError(e.message || 'Network error');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [receiverUuid, token, rangeSeconds]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!refreshSeconds) return;
    const id = setInterval(() => { load(); }, refreshSeconds * 1000);
    return () => clearInterval(id);
  }, [load, refreshSeconds]);

  const sample = (k) => series[k] || [];
  const hasSamples = (pts) => pts.some(p => p && p.v != null);
  const v = (k) => latest?.values?.[k];

  // Restart markers come from the receiver's own session_start_ms, reported as epoch-ms —
  // authoritative and timezone-free.
  //
  // Connection events are deliberately NOT used here. They are a weaker signal (a socket can
  // drop and reconnect without the process restarting — network blip, backend redeploy), and
  // ReceiverConnectionEvent.timestamp is a LocalDateTime, which serialises with no zone; JS
  // then parses it as the VIEWER's local time even though the server is UTC. That is the exact
  // bug behind the "Last Active 1 hour ago" regression. A marker an hour away from the cliff it
  // labels is worse than no marker, so this waits for the event feed to emit a real instant.
  const markers = restarts
    .filter(ts => Number.isFinite(ts))
    .map(ts => ({ ts, label: 'restart' }))
    .sort((a, b) => a.ts - b.ts);

  return (
    <div className="perf-charts">
      {isAviotrack === false && (
        <RequiresAviotrackBanner latestVersion={latestReceiverVersion} onNavigate={onNavigate} />
      )}
      <div className="perf-charts-header">
        <div className="perf-charts-controls">
          <div className="perf-control-group">
            <span className="perf-control-label">Range</span>
            {RANGE_PRESETS.map(r => (
              <button
                key={r.label}
                className={`perf-pill ${r.seconds === rangeSeconds ? 'active' : ''}`}
                onClick={() => setRangeSeconds(r.seconds)}
              >{r.label}</button>
            ))}
          </div>
          <div className="perf-control-group">
            <span className="perf-control-label">Refresh</span>
            {REFRESH_PRESETS.map(r => (
              <button
                key={r.label}
                className={`perf-pill ${r.seconds === refreshSeconds ? 'active' : ''}`}
                onClick={() => setRefreshSeconds(r.seconds)}
              >{r.label}</button>
            ))}
          </div>
          <button className="perf-pill" onClick={load} disabled={loading}>
            {loading ? '…' : 'Refresh now'}
          </button>
        </div>
        {(updatedAt || error) && (
          <div className="perf-charts-meta">
            {/* An error on the FIRST load leaves updatedAt null. Gating the whole line on
                updatedAt meant a failed request rendered no message at all — just charts
                reading "No data", which is indistinguishable from a quiet receiver. */}
            {updatedAt && <>Updated {updatedAt.toLocaleTimeString()}</>}
            {error && <span className="perf-charts-error">{updatedAt ? ' · ' : ''}{error}</span>}
          </div>
        )}
      </div>

      {anchoredRange && (
        <div className="perf-anchored-banner" role="status">
          <strong>This receiver is not reporting now — showing its most recent data instead.</strong>{' '}
          The selected range held nothing, so these charts cover{' '}
          {formatRangeLabel(anchoredRange.from, anchoredRange.to)}. Treat every value here as
          historical, not live.
        </div>
      )}

      <FreshnessPanel latest={latest} />

      <div className={`perf-charts-live${latest && latest.live === false ? ' perf-charts-live-stale' : ''}`}>
        <LiveStat label="msg/s"     value={fmt(v('msg_rx_rate'), 1)} />
        <LiveStat label="aircraft"  value={fmt(v('aircraft_now'), 0)} />
        <LiveStat label="range nm"  value={fmt(v('max_range_nm'), 0)} />
        <LiveStat label="gain dB"   value={fmt(v('current_gain_db'), 1)} />
        <LiveStat label="CPU %"     value={fmt(v('cpu_pct'), 0)} />
        <LiveStat label="mem %"     value={fmt(v('mem_pct'), 0)} />
        <LiveStat label="temp °C"   value={fmt(v('temp_c'), 0)} />
      </div>

      <div className="perf-charts-grid">
        {/* The three that answer "is it hearing more?" go full width at a 1-minute step —
            these are the metrics a demodulator change actually moves, and a 360px cell at a
            5-minute bucket is too coarse to see it. */}
        <PerfChart
          wide
          markers={markers}
          title="Aircraft tracked"
          unit="count"
          series={[
            { name: 'now',  samples: sample('aircraft_now'),       variant: 'primary' },
            { name: 'peak', samples: sample('aircraft_peak_1m'),   variant: 'muted' },
            // Kept separate from "now" (ADS-B) so the count stays comparable with older receivers.
            ...(hasSamples(sample('aircraft_modes_now'))
              ? [{ name: 'Mode-S only', samples: sample('aircraft_modes_now'), variant: 'secondary' }]
              : []),
          ]}
        />
        <PerfChart
          wide
          markers={markers}
          title="Max range"
          unit="nm"
          series={[
            { name: 'max range', samples: sample('max_range_nm'), variant: 'primary' },
          ]}
        />
        <PerfChart
          wide
          markers={markers}
          title="Messages / sec"
          unit="msg/s"
          series={[
            { name: 'rx rate', samples: sample('msg_rx_rate'), variant: 'primary' },
            { name: 'dropped/s', samples: sample('msg_dropped'),  variant: 'danger' },
          ]}
        />
        {/* Input-side drops: the only series that separates "not sensitive enough" from
            "dropping samples before the decoder ever saw them". The receiver reports running
            session totals; charted raw, a burst is a barely visible step on a huge baseline,
            so they are differenced into a per-minute rate. */}
        <PerfChart
          wide
          markers={markers}
          title="Input drops by cause"
          unit="drops/min"
          yMin={0}
          series={[
            // Ring-full first: it is the one that means "the demodulator cannot keep up".
            { name: 'ring full (worker behind)', samples: perMinute(sample('ring_full_drops')), variant: 'danger' },
            { name: 'oversized (rejected)',      samples: perMinute(sample('oversized_drops')), variant: 'secondary' },
            { name: 'USB/firmware',              samples: perMinute(sample('dropped_samples')), variant: 'muted' },
          ]}
        />
        <PerfChart
          title="RSSI"
          unit="dBFS"
          series={[
            { name: 'min',   samples: sample('rssi_min_dbfs'),    variant: 'muted' },
            { name: 'avg',   samples: sample('rssi_avg_dbfs'),    variant: 'primary' },
            { name: 'max',   samples: sample('rssi_max_dbfs'),    variant: 'secondary' },
            { name: 'noise', samples: sample('noise_floor_dbfs'), variant: 'danger' },
          ]}
        />
        <PerfChart
          title="System"
          unit="%/°C"
          yMin={0}
          yMax={100}
          series={[
            { name: 'CPU %', samples: sample('cpu_pct'), variant: 'primary' },
            { name: 'mem %', samples: sample('mem_pct'), variant: 'secondary' },
            { name: 'temp °C', samples: sample('temp_c'), variant: 'danger' },
          ]}
        />
        <PerfChart
          title="Gain"
          unit="dB"
          yMin={0}
          yMax={50}
          series={[
            { name: 'gain', samples: sample('current_gain_db'), variant: 'primary' },
          ]}
        />
      </div>

      <ConnectionEventsList events={events} />
    </div>
  );
}

/**
 * Per-receiver slice of the same connection-event log shown on the admin
 * page. Read-only — refreshes on the same cadence as the rest of PerfCharts.
 */
function ConnectionEventsList({ events }) {
  if (!events || events.length === 0) {
    return (
      <div className="perf-events-section">
        <h3 className="perf-events-title">Connection events</h3>
        <div className="perf-events-empty">No recent connection events for this receiver.</div>
      </div>
    );
  }
  return (
    <div className="perf-events-section">
      <h3 className="perf-events-title">Connection events <span className="perf-events-count">({events.length})</span></h3>
      <div className="perf-events-table-wrapper">
        <table className="perf-events-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Source</th>
              <th>Reason</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => (
              <tr key={`${ev.connectionId || 'x'}-${ev.timestamp || i}-${i}`}>
                <td className="perf-events-when">{formatEventTime(ev.timestamp)}</td>
                <td className={`perf-events-type ${eventTypeClass(ev.eventType)}`}>{ev.eventType}</td>
                <td className="perf-events-src">{ev.sourceIp}{ev.sourcePort ? `:${ev.sourcePort}` : ''}</td>
                <td>{ev.reason || ''}</td>
                <td className="perf-events-msg">{ev.message || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatEventTime(s) {
  if (!s) return '—';
  // The backend serializes LocalDateTime as "yyyy-MM-ddTHH:mm:ss" without Z;
  // append Z so JS parses it as UTC and renders in browser-local time.
  const isoZ = /Z|[+-]\d\d:\d\d$/.test(s) ? s : s + 'Z';
  const d = new Date(isoZ);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function eventTypeClass(t) {
  if (!t) return '';
  if (t === 'DISCONNECTED' || t === 'UUID_INVALID' || t === 'RECEIVER_REAPED') return 'perf-events-type-bad';
  if (t === 'RECEIVER_STALE' || t === 'UNKNOWN_RECEIVER_DETECTED') return 'perf-events-type-warn';
  if (t === 'UUID_RECEIVED' || t === 'FIRST_BEAST_MESSAGE') return 'perf-events-type-good';
  return '';
}

function LiveStat({ label, value }) {
  return (
    <div className="perf-live-stat">
      <div className="perf-live-stat-value">{value ?? '—'}</div>
      <div className="perf-live-stat-label">{label}</div>
    </div>
  );
}

/**
 * Human label for an anchored chart window, e.g. "26 Sep 14:30 – 15:30".
 * Both ends are ISO-8601 UTC instants from the backend, so Date parses them correctly;
 * they are rendered in the viewer's locale/zone like every other time on this page.
 */
function formatRangeLabel(fromIso, toIso) {
  const from = fromIso ? new Date(fromIso) : null;
  const to = toIso ? new Date(toIso) : null;
  if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return 'an earlier period';
  }
  const sameDay = from.toDateString() === to.toDateString();
  const dayPart = from.toLocaleDateString([], { day: 'numeric', month: 'short' });
  const fromTime = from.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const toTime = to.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return sameDay
    ? `${dayPart} ${fromTime} – ${toTime}`
    : `${dayPart} ${fromTime} – ${to.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${toTime}`;
}

/**
 * Turn a cumulative-per-session counter into a per-minute rate between consecutive samples.
 * A drop in the counter is a receiver restart resetting it to zero, so the new value is
 * itself the count since the reset — never a negative rate. Across a gap in reporting the
 * rate is the average over the gap. The first sample has no predecessor and yields no point.
 */
function perMinute(samples) {
  const pts = samples.filter(p => p && p.v != null).sort((a, b) => a.ts - b.ts);
  const out = [];
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const minutes = (cur.ts - prev.ts) / 60000;
    if (minutes <= 0) continue;
    const delta = cur.v >= prev.v ? cur.v - prev.v : cur.v;
    out.push({ ts: cur.ts, v: delta / minutes });
  }
  return out;
}

function fmt(v, digits) {
  if (v == null || Number.isNaN(v)) return null;
  return Number(v).toFixed(digits);
}

// STATS heartbeat is sent on a 60s cadence; allow a small grace window before
// turning the indicator red. Same idea for the receiver-side config heartbeat.
const STATS_FRESH_WINDOW_S = 90;
const CONFIG_HEARTBEAT_FRESH_WINDOW_S = 90;

/**
 * Strip above the live stats showing four signals operators ask about most:
 *  - last STATS heartbeat (seconds ago, green tick if fresh)
 *  - last CFG-VER from receiver (seconds ago, green tick if fresh)
 *  - last receiver restart (timestamp, derived from `uptime_s`)
 *  - last config update by admin (timestamp)
 *
 * All values are derived from the {@code getReceiverStatsLatest} response.
 * If any field is missing the cell shows a long-dash; we never fabricate.
 */
function FreshnessPanel({ latest }) {
  const nowMs = Date.now();
  const statsTsMs = latest?.ts ?? null;
  const statsAgeS = statsTsMs ? Math.max(0, Math.floor((nowMs - statsTsMs) / 1000)) : null;

  const uptimeS = latest?.values?.uptime_s;
  const lastRestartMs = (statsTsMs && Number.isFinite(Number(uptimeS)))
    ? statsTsMs - Number(uptimeS) * 1000
    : null;

  const cfgHeartbeatMs = latest?.configHeartbeatAt
    ? new Date(latest.configHeartbeatAt).getTime()
    : null;
  const cfgHeartbeatAgeS = cfgHeartbeatMs
    ? Math.max(0, Math.floor((nowMs - cfgHeartbeatMs) / 1000))
    : null;

  const cfgUpdatedMs = latest?.configUpdatedAt
    ? new Date(latest.configUpdatedAt).getTime()
    : null;

  return (
    <div className="perf-freshness-panel">
      <FreshnessAge
        label="Last STATS"
        ageS={statsAgeS}
        freshWindowS={STATS_FRESH_WINDOW_S}
      />
      <FreshnessAge
        label="Last CFG-VER"
        ageS={cfgHeartbeatAgeS}
        freshWindowS={CONFIG_HEARTBEAT_FRESH_WINDOW_S}
      />
      <FreshnessTimestamp
        label="Last restart"
        ms={lastRestartMs}
      />
      <FreshnessTimestamp
        label="Last config update"
        ms={cfgUpdatedMs}
      />
    </div>
  );
}

function FreshnessAge({ label, ageS, freshWindowS }) {
  const display = ageS == null ? '—' : formatAgeShort(ageS);
  // null age renders neutral (no tick / cross) — we don't know yet.
  const indicator = ageS == null ? '' : (ageS <= freshWindowS ? '✓' : '✕');
  const className = ageS == null
    ? 'perf-freshness-cell'
    : (ageS <= freshWindowS
        ? 'perf-freshness-cell perf-freshness-fresh'
        : 'perf-freshness-cell perf-freshness-stale');
  return (
    <div className={className}>
      <div className="perf-freshness-label">{label}</div>
      <div className="perf-freshness-value">
        {indicator && <span className="perf-freshness-indicator">{indicator}</span>}
        {display}
      </div>
    </div>
  );
}

function FreshnessTimestamp({ label, ms }) {
  const display = ms == null || Number.isNaN(ms) ? '—' : formatAbsolute(ms);
  return (
    <div className="perf-freshness-cell">
      <div className="perf-freshness-label">{label}</div>
      <div className="perf-freshness-value">{display}</div>
    </div>
  );
}

function formatAgeShort(s) {
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} d`;
}

function formatAbsolute(ms) {
  // Browser-local timezone — operators are fine reading their own clock.
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default PerfCharts;
